/**
 * @fileoverview This polyfill provides the `requestVideoFrameCallback` API for HTMLVideoElement
 * in browsers that do not natively support it but do support `getVideoPlaybackQuality`.
 * It attempts to simulate the callback functionality using `requestAnimationFrame` and
 * monitoring frame changes via presentation/painted frame counters or quality metrics.
 */
(() => {
    // Check for native support or essential requirement.
    // If native `requestVideoFrameCallback` exists OR if `getVideoPlaybackQuality` (needed for the polyfill) is missing,
    // then we should not proceed with the polyfill.
    if ('requestVideoFrameCallback' in HTMLVideoElement.prototype || !('getVideoPlaybackQuality' in HTMLVideoElement.prototype)) {
        return;
    }
    // A counter to generate unique handle IDs for each requested callback.
    let nextCallbackHandleID = 0;
    // Get a reference to the HTMLVideoElement prototype to add the new methods.
    // We use `any` here to allow adding properties that are not yet officially on the prototype.
    const videoPrototype = HTMLVideoElement.prototype;
    // Initialize the private map on the prototype for tracking active polyfill callbacks.
    // Note: While added to the prototype publicly, it's intended for internal use by this polyfill.
    videoPrototype._rvfcPolyfillMap = {};
    /**
     * @public
     * Polyfills the `requestVideoFrameCallback` method on HTMLVideoElement.
     * It uses `requestAnimationFrame` and monitors frame count changes to simulate the callback.
     *
     * @param {VideoFrameRequestCallback} callback - The function to call when a new frame is presented.
     * @returns {number} A unique ID (handle) for the registered callback.
     */
    videoPrototype.requestVideoFrameCallback = function (callback) {
        // Get a reference to the current video element instance.
        const videoElement = this;
        // Increment the counter and use the new value as the unique handle for this request.
        const handleID = ++nextCallbackHandleID;
        // Record the time when the callback was requested (start of the monitoring process).
        const requestStartTime = performance.now();
        // Get the initial quality statistics (VideoPlaybackQuality) to establish a baseline.
        // We cast it to include the required proprietary property for frame time tracking.
        const initialQualityStats = videoElement.getVideoPlaybackQuality();
        // Determine the baseline presented/rendered frame count.
        // It prioritizes proprietary properties (mozPresentedFrames, mozPaintedFrames) and falls back
        // to calculating presented frames from the quality stats (total - dropped).
        const baselineFrameCount = (videoElement.mozPresentedFrames ||
            videoElement.mozPaintedFrames ||
            initialQualityStats.totalVideoFrames - initialQualityStats.droppedVideoFrames);
        /**
         * @private
         * The core check function, executed via `requestAnimationFrame`, that monitors for frame presentation change.
         * @param {DOMHighResTimeStamp} previousTime - The timestamp of the *previous* `requestAnimationFrame` execution.
         * @param {DOMHighResTimeStamp} currentTime - The timestamp of the *current* `requestAnimationFrame` execution.
         */
        const checkFramePresentation = (previousTime, currentTime) => {
            // Check if the handle ID is still present in the map, which means the callback has not been canceled.
            if (!(handleID in videoElement._rvfcPolyfillMap)) {
                return;
            }
            // Get the latest video playback quality statistics for comparison.
            const latestQualityStats = videoElement.getVideoPlaybackQuality();
            // Recalculate the current presented frame count using the same priority fallback logic.
            const currentPresentedFrames = (videoElement.mozPresentedFrames ||
                videoElement.mozPaintedFrames ||
                latestQualityStats.totalVideoFrames - latestQualityStats.droppedVideoFrames);
            // If the current presented frame count is greater than the baseline, a new frame has been displayed.
            if (currentPresentedFrames > baselineFrameCount) {
                // Calculate the processing duration (time between frame ready and display).
                // Priority is given to proprietary time property, then the difference in total frame delays.
                const processingDurationSeconds = (videoElement.mozFrameDelay ||
                    latestQualityStats.totalFrameDelay - initialQualityStats.totalFrameDelay ||
                    0);
                // Calculate the time difference between the current and previous animation frame timestamp.
                const deltaTime = currentTime - previousTime;
                callback(currentTime, {
                    // Estimated actual presentation time (current time + processing time converted to ms).
                    presentationTime: currentTime + processingDurationSeconds * 1000,
                    // Expected time of the *next* frame display (approximated by current time + time elapsed since last check).
                    expectedDisplayTime: currentTime + deltaTime,
                    // Video resolution dimensions.
                    width: videoElement.videoWidth,
                    height: videoElement.videoHeight,
                    // Estimated media time at presentation (current video time + time elapsed since last check, converted to seconds).
                    mediaTime: Math.max(0, videoElement.currentTime || 0) + deltaTime / 1000,
                    // The count of frames that have been presented so far.
                    presentedFrames: currentPresentedFrames,
                    // The estimated processing duration/time for this frame.
                    processingDuration: processingDurationSeconds,
                });
                // Once executed, remove the handle from the map as this is a one-time callback.
                delete videoElement._rvfcPolyfillMap[handleID];
            }
            else {
                // If the frame count hasn't changed, schedule the check function to run again on the next animation frame.
                // The result of `requestAnimationFrame` (the new request handle) is stored in the map, overwriting the previous one.
                videoElement._rvfcPolyfillMap[handleID] = requestAnimationFrame(newerTimestamp => checkFramePresentation(currentTime, newerTimestamp));
            }
        };
        // Start the monitoring loop by scheduling the first check on the next animation frame.
        // Store the handle from `requestAnimationFrame` so it can be canceled later.
        videoElement._rvfcPolyfillMap[handleID] = requestAnimationFrame(newerTimestamp => checkFramePresentation(requestStartTime, newerTimestamp));
        return handleID;
    };
    /**
     * @public
     * Polyfills the `cancelVideoFrameCallback` method on HTMLVideoElement.
     * Cancels the active frame monitoring loop associated with the given handleID.
     *
     * @param {number} handleID - The ID (handle) of the callback to cancel, returned by `requestVideoFrameCallback`.
     * @returns {void}
     */
    videoPrototype.cancelVideoFrameCallback = function (handleID) {
        // Get a reference to the current video element instance.
        const videoElement = this;
        // Check if the provided handle ID exists in the internal map.
        if (handleID in videoElement._rvfcPolyfillMap) {
            // Get the `requestAnimationFrame` handle stored in the map and cancel the animation frame request.
            cancelAnimationFrame(videoElement._rvfcPolyfillMap[handleID]);
            // Remove the entry from the internal map.
            delete videoElement._rvfcPolyfillMap[handleID];
        }
    };
})();
export {};
//# sourceMappingURL=request-video-frame-callback.js.map